import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

// Per-user notification preferences. Drives the alarm fan-out queue:
// - Channel selection per business-hours window
// - Active hours per timezone — outside-window contacts still get queued but the
//   worker no-ops on them at fire time
// Empty channel arrays effectively mute the user during that window.

export type NotificationChannel = 'sms' | 'push' | 'email' | 'call';
export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface NotificationPreferences {
  timezone?: string;
  business_hours?: {
    days?: DayOfWeek[];
    start?: string; // HH:MM
    end?: string;
  };
  channels?: {
    business_hours?: NotificationChannel[];
    after_hours?: NotificationChannel[];
  };
}

export function useMyNotificationPreferences() {
  const api = useApi();
  return useQuery({
    queryKey: ['me', 'notification-preferences'],
    queryFn: () =>
      api<{ preferences: NotificationPreferences | null }>('/me/notification-preferences'),
    staleTime: 60_000,
  });
}

export function useUpdateMyNotificationPreferences() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: NotificationPreferences) =>
      api<{ preferences: NotificationPreferences }>('/me/notification-preferences', {
        method: 'PUT',
        body: JSON.stringify(prefs),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'notification-preferences'] }),
  });
}
